// A CSV reader for bank and fuel exports, in a plain .ts module so Node's test
// runner can check it.
//
// Why hand-rolled rather than a dependency: the repo has never added a runtime
// dependency for something this size, and the failure modes that actually bite on
// a South African bank export are all quoting and separator issues that fit in
// forty lines. What it must get right:
//
//   - quoted fields containing the separator, which every FNB description does
//   - doubled quotes inside a quoted field
//   - CRLF, because these files come off Windows
//   - semicolon separators, which some FNB exports use
//   - a preamble of account-header lines above the real header row, which the FNB
//     CSV always has, so the header is found rather than assumed to be line 1
//
// It does not try to guess types. Everything comes back as a string and the
// mapping layer decides.

export function detectSeparator(text: string): string {
  const line = text.split(/\r?\n/).find((l) => l.trim().length > 0) ?? ''
  const counts: Record<string, number> = { ',': 0, ';': 0, '\t': 0 }
  let inQuotes = false
  for (const ch of line) {
    if (ch === '"') inQuotes = !inQuotes
    else if (!inQuotes && ch in counts) counts[ch] += 1
  }
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0] || ','
}

export function parseCsv(text: string, separator?: string): string[][] {
  const sep = separator ?? detectSeparator(text)
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let inQuotes = false

  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"'
          i++
        } else inQuotes = false
      } else field += ch
      continue
    }
    if (ch === '"') inQuotes = true
    else if (ch === sep) {
      row.push(field)
      field = ''
    } else if (ch === '\n') {
      row.push(field)
      rows.push(row)
      row = []
      field = ''
    } else if (ch !== '\r') field += ch
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field)
    rows.push(row)
  }
  return rows.filter((r) => r.some((c) => c.trim().length > 0))
}

/**
 * Find the header row.
 *
 * An FNB CSV opens with account-header lines that have one or two populated
 * columns, then the real header. Taking row 0 blindly gives a "header" of
 * ["Account Number", "62..."] and every subsequent mapping is nonsense, so the
 * header is the first row with at least three non-empty cells whose count of
 * non-empty cells is not beaten by the row after it.
 */
export function findHeaderRow(rows: string[][]): number {
  let best = 0
  let bestScore = -1
  for (let i = 0; i < Math.min(rows.length, 25); i++) {
    const filled = rows[i].filter((c) => c.trim().length > 0).length
    if (filled >= 3 && filled > bestScore) {
      bestScore = filled
      best = i
    }
  }
  return bestScore === -1 ? 0 : best
}

export type Sheet = { headers: string[]; rows: Record<string, string>[] }

export function toSheet(text: string): Sheet {
  const rows = parseCsv(text)
  if (rows.length === 0) return { headers: [], rows: [] }
  const h = findHeaderRow(rows)
  const headers = rows[h].map((c, i) => c.trim() || `column ${i + 1}`)
  const body = rows.slice(h + 1).map((r) => {
    const o: Record<string, string> = {}
    headers.forEach((name, i) => {
      o[name] = (r[i] ?? '').trim()
    })
    return o
  })
  return { headers, rows: body }
}

/**
 * A South African bank or fuel export writes money as "1 234,56", "1,234.56",
 * "R1 234.56" or "(123.45)" for a debit. Returning null rather than 0 for
 * anything unreadable is deliberate: a silent zero in a statement line would
 * clear a deposit that never cleared.
 */
export function parseAmount(raw: string | null | undefined): number | null {
  if (raw === null || raw === undefined) return null
  let s = String(raw).trim()
  if (s === '') return null
  let negative = false
  if (/^\(.*\)$/.test(s)) {
    negative = true
    s = s.slice(1, -1)
  }
  s = s.replace(/[Rr]|\s| /g, '')
  if (s.startsWith('-')) {
    negative = true
    s = s.slice(1)
  }
  // Whichever of . and , appears last is the decimal separator.
  const lastDot = s.lastIndexOf('.')
  const lastComma = s.lastIndexOf(',')
  if (lastComma > lastDot) s = s.replace(/\./g, '').replace(',', '.')
  else s = s.replace(/,/g, '')
  const n = Number(s)
  if (!Number.isFinite(n)) return null
  return negative ? -n : n
}

/**
 * Dates on these exports are dd/mm/yyyy far more often than mm/dd/yyyy, and
 * getting it wrong moves a deposit by up to eleven days, which is longer than the
 * clearing window. So an ambiguous slash date is read day-first, which is the
 * South African convention, and an ISO date is read as ISO.
 */
export function parseDate(raw: string | null | undefined): string | null {
  if (!raw) return null
  const s = String(raw).trim()
  if (s === '') return null
  const iso = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/)
  if (iso) return `${iso[1]}-${iso[2].padStart(2, '0')}-${iso[3].padStart(2, '0')}`
  const dmy = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})/)
  if (dmy) {
    const year = dmy[3].length === 2 ? `20${dmy[3]}` : dmy[3]
    return `${year}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`
  }
  const parsed = Date.parse(s)
  if (Number.isNaN(parsed)) return null
  return new Date(parsed).toISOString().slice(0, 10)
}

/** Best-guess column for a field, so the mapper opens with something sensible. */
export function guessColumn(headers: string[], candidates: string[]): string | '' {
  const norm = (v: string) => v.toLowerCase().replace(/[^a-z0-9]/g, '')
  for (const c of candidates) {
    const target = norm(c)
    const exact = headers.find((h) => norm(h) === target)
    if (exact) return exact
  }
  for (const c of candidates) {
    const target = norm(c)
    const partial = headers.find((h) => norm(h).includes(target))
    if (partial) return partial
  }
  return ''
}
